import React, { useState, useEffect } from 'react';
import { Box, Container, Grid, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import WbButton from '../../../components/common/WbButton';
import WbLoader from '../../../components/common/WbLoader';
import KioskLayout from '../../../components/kiosk/KioskLayout';
import { useKiosk } from '../../../contexts/KioskContext';
import kioskApi from '../../../services/api/kiosk/kioskApi';
import paths from '../../../routes/paths';
import { surveyUrlForCurrentOrigin } from '../kioskUtils';
import '../KioskStyles.scss';

export default function ParentSurveys() {
  const { schoolSlug } = useParams();
  const { school, loading: schoolLoading } = useKiosk();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSurveys = async () => {
      try {
        setLoading(true);
        const verifiedParent = JSON.parse(localStorage.getItem('kiosk-verified-parent') || '{}');
        if (!verifiedParent.parentId) {
          navigate(paths.kioskParentWelcome.replace(':schoolSlug', schoolSlug));
          return;
        }

        if (!school?.schoolId) {
          setError('School information not available');
          setLoading(false);
          return;
        }
        const data = await kioskApi.getParentSurveys(school.schoolId, verifiedParent.parentId);
        setSurveys(data || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load surveys');
      } finally {
        setLoading(false);
      }
    };

    if (!schoolLoading) {
      loadSurveys();
    }
  }, [schoolSlug, schoolLoading, navigate]);

  const handleSurveySelect = (surveyUrl) => {
    const baseUrl = surveyUrlForCurrentOrigin(surveyUrl);
    try {
      const url = new URL(baseUrl);
      url.searchParams.set('kiosk', 'parent');
      url.searchParams.set('schoolSlug', schoolSlug);
      window.location.href = url.toString();
    } catch {
      window.location.href = baseUrl;
    }
  };

  if (schoolLoading || loading) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  if (error) {
    return (
      <KioskLayout>
        <Container maxWidth="md">
          <div>
            <Alert severity="error">{error}</Alert>
          </div>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <WbButton
              CustomButtonText="Back to Welcome"
              onClick={() => navigate(paths.kioskParentWelcome.replace(':schoolSlug', schoolSlug))}
              className="kiosk-button-large"
            />
          </Box>
        </Container>
      </KioskLayout>
    );
  }

  if (surveys.length === 0) {
    return (
      <KioskLayout>
        <Container maxWidth="md">
          <div className="kiosk-title">
            No Surveys Available
          </div>
          <div className="kiosk-subtitle">
            There are no open surveys available at this time.
          </div>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <WbButton
              CustomButtonText="Back to Welcome"
              onClick={() => navigate(paths.kioskParentWelcome.replace(':schoolSlug', schoolSlug))}
              className="kiosk-button-large"
            />
          </Box>
        </Container>
      </KioskLayout>
    );
  }

  return (
    <KioskLayout>
      <Container maxWidth="lg">
        <div className="kiosk-title">
          Select a Survey
        </div>
        <div className="kiosk-subtitle">
          Please choose a survey to complete
        </div>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {surveys.map((survey, index) => (
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <div
                key={index}
                className="kiosk-survey-item"
                onClick={() => handleSurveySelect(survey.url)}
              >
                <div className="result-name">{survey.name || `Survey ${index + 1}`}</div>
                {survey.dueDate && (
                  <div className="result-class"><strong>Due Date:</strong> {survey.dueDate}</div>
                )}
                <WbButton
                  CustomButtonText="Start Survey"
                  onClick={() => handleSurveySelect(survey.url)}
                  className="kiosk-button-small"
                />
              </div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </KioskLayout>
  );
}
